import type { Form } from 'antd'
import type { InvoiceFormValues, InvoiceProduct, InvoiceTax } from '@api/useSupplierReconciliation/types/invoice'
import { multiplyWithPrecision, addWithPrecision, roundToPrecision } from '../utils/formatters'

export function useInvoiceCalculations() {
  const calculateTotals = (form: Form<InvoiceFormValues>) => {
    const products = form.getFieldValue('products') || []
    const taxes = form.getFieldValue('taxes') || []
    const billingMethod = form.getFieldValue('billingBy') || 'net'

    const productTotals: number[] = []
    const updatedProducts = products.map((product: InvoiceProduct) => {
      if (product) {
        const volume = billingMethod === 'gross' ? product.grossVol : product.netVol
        const unitPrice = product.unitPrice
        if (volume != null && volume > 0 && unitPrice != null && unitPrice > 0) {
          // Use precision-safe multiplication for product totals
          const total = multiplyWithPrecision(volume, unitPrice, 2)
          productTotals.push(total)
          return { ...product, total }
        } else {
          return { ...product, total: undefined }
        }
      }
      return product
    })

    // Collect all tax totals for precision-safe addition
    const taxTotals: (number | string)[] = []
    for (const tax of taxes) {
      if (tax && tax.total != null) {
        taxTotals.push(tax.total)
      }
    }

    // Use precision-safe addition for all totals
    const productTotal = addWithPrecision(productTotals, 2)
    const taxTotal = addWithPrecision(taxTotals, 2)
    const grandTotal = addWithPrecision([productTotal, taxTotal], 2)

    form.setFieldsValue({
      products: updatedProducts,
      productTotal,
      taxTotal,
      grandTotal,
    })
  }

  const calculateProductTotal = (
    index: number,
    field: keyof InvoiceProduct,
    value: number | string | null,
    form: Form<InvoiceFormValues>
  ) => {
    if (value === null || value === undefined) return

    const products = form.getFieldValue('products') || []
    const product = { ...(products[index] || {}) }
    const billingMethod = form.getFieldValue('billingBy') || 'net'
    const taxes = form.getFieldValue('taxes') || []

    if (field === 'unitPrice' || field === 'netVol' || field === 'grossVol') {
      const netVol = field === 'netVol' ? (value as number) : product.netVol
      const grossVol = field === 'grossVol' ? (value as number) : product.grossVol
      const unitPrice = field === 'unitPrice' ? (value as number) : product.unitPrice

      const volume = billingMethod === 'gross' ? grossVol : netVol
      if (volume != null && volume > 0 && unitPrice != null && unitPrice > 0) {
        // Use precision-safe multiplication for product total calculation
        product.total = multiplyWithPrecision(volume, unitPrice, 2)
      } else {
        product.total = undefined
      }

      if (field === 'netVol' || field === 'grossVol') {
        const productId = products[index]?.product
        if (productId) {
          const updatedTaxes = taxes.map((tax: InvoiceTax) => {
            if (tax.product === productId) {
              const newVolume = billingMethod === 'gross' ? grossVol : netVol
              return {
                ...tax,
                productVolume: newVolume,
                amount: newVolume,
                total:
                  tax.perUnit != null && newVolume != null
                    ? multiplyWithPrecision(newVolume, tax.perUnit, 2)
                    : tax.total,
              }
            }
            return tax
          })
          form.setFieldsValue({ taxes: updatedTaxes })
        }
      }
    } else if (field === 'product') {
      product.product = value as string

      setTimeout(() => {
        const currentProducts = form.getFieldValue('products') || []
        currentProducts.forEach((_: InvoiceProduct, idx: number) => {
          form.validateFields([['products', idx, 'product']])
        })
      }, 0)
    }

    const updatedProducts = [...products]
    updatedProducts[index] = { ...product, [field]: value }

    form.setFieldsValue({
      products: updatedProducts,
    })
  }

  const handleTaxProductSelect = (taxIndex: number, productId: string | null, form: Form<InvoiceFormValues>) => {
    const taxes = form.getFieldValue('taxes') || []
    const products = form.getFieldValue('products') || []
    const billingMethod = form.getFieldValue('billingBy') || 'net'

    if (!productId) {
      form.setFieldsValue({
        taxes: taxes.map((tax: InvoiceTax, idx: number) =>
          idx === taxIndex ? { ...tax, product: undefined, productVolume: undefined } : tax
        ),
      })
      return
    }

    const selectedProduct = products.find((p: InvoiceProduct) => p.product === productId)
    if (selectedProduct) {
      const volume = billingMethod === 'gross' ? selectedProduct.grossVol : selectedProduct.netVol
      const updatedTaxes = [...taxes]
      const currentTax = updatedTaxes[taxIndex]
      const newAmount = volume
      const existingPerUnit = currentTax.perUnit

      updatedTaxes[taxIndex] = {
        ...currentTax,
        product: productId,
        productVolume: volume,
        amount: newAmount,
        total:
          existingPerUnit != null && newAmount != null
            ? multiplyWithPrecision(newAmount, existingPerUnit, 2)
            : currentTax.total,
      }

      form.setFieldsValue({ taxes: updatedTaxes })
    }
  }

  const handleBillingMethodChange = (billingMethod: string, form: Form<InvoiceFormValues>) => {
    const taxes = form.getFieldValue('taxes') || []
    const products = form.getFieldValue('products') || []

    const updatedTaxes = taxes.map((tax: InvoiceTax) => {
      if (tax.product) {
        const selectedProduct = products.find((p: InvoiceProduct) => p.product === tax.product)
        if (selectedProduct) {
          const volume = billingMethod === 'gross' ? selectedProduct.grossVol : selectedProduct.netVol
          return {
            ...tax,
            productVolume: volume,
            amount: volume,
          }
        }
      }
      return tax
    })

    form.setFieldsValue({ taxes: updatedTaxes })
  }

  const updateTaxAmount = (index: number, value: number | null, form: Form<InvoiceFormValues>) => {
    const taxes = form.getFieldValue('taxes') || []
    const updatedTaxes = [...taxes]
    updatedTaxes[index] = { ...updatedTaxes[index], amount: value }
    form.setFieldsValue({ taxes: updatedTaxes })

    const perUnit = updatedTaxes[index]?.perUnit
    if (perUnit != null && value != null) {
      // Use precision-safe multiplication for tax total calculation
      updatedTaxes[index].total = multiplyWithPrecision(value, perUnit, 2)
      form.setFieldsValue({ taxes: updatedTaxes })
    }
  }

  const updateTaxPerUnit = (index: number, value: number | null, form: Form<InvoiceFormValues>) => {
    const taxes = form.getFieldValue('taxes') || []
    const updatedTaxes = [...taxes]
    updatedTaxes[index] = { ...updatedTaxes[index], perUnit: value }

    const amount = updatedTaxes[index]?.amount
    if (amount != null && value != null) {
      // Use precision-safe multiplication for tax total calculation
      updatedTaxes[index].total = multiplyWithPrecision(amount, value, 2)
    }

    form.setFieldsValue({ taxes: updatedTaxes })
  }

  const updateTaxTotal = (index: number, value: number | null, form: Form<InvoiceFormValues>) => {
    const taxes = form.getFieldValue('taxes') || []
    const updatedTaxes = [...taxes]
    updatedTaxes[index] = { ...updatedTaxes[index], total: value }
    form.setFieldsValue({ taxes: updatedTaxes })
  }

  const clearTaxProductReferences = (productId: string, form: Form<InvoiceFormValues>) => {
    const taxes = form.getFieldValue('taxes') || []
    const updatedTaxes = taxes.map((tax: InvoiceTax) => {
      if (tax.product === productId) {
        return {
          ...tax,
          product: undefined,
          productVolume: undefined,
        }
      }
      return tax
    })

    form.setFieldsValue({ taxes: updatedTaxes })
  }

  return {
    calculateTotals,
    calculateProductTotal,
    handleTaxProductSelect,
    handleBillingMethodChange,
    updateTaxAmount,
    updateTaxPerUnit,
    updateTaxTotal,
    clearTaxProductReferences,
  }
}
